// @ts-nocheck

import React from 'react';
import { Paper } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridColumnGroupingModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';
import dataTableStyles from './datatable.module.css';

interface Column extends GridColDef {
  id: string;
  label: string;
  groupId?: string;
}

interface ColumnGroup {
  id: string;
  label: string;
  colspan: number;
}

interface SortParam {
  field: string;
  sort: 'asc' | 'desc' | undefined;
}
interface DataTableProps {
  columns: Column[];
  columnGroups?: ColumnGroup[];
  data: any[];
  page: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort?: (param: SortParam) => void ;
  disableColumnMenu: boolean;
  disableColumnResize: boolean;
  renderActions?: (row: any) => React.ReactNode;
  enableZebraStripes?: boolean;
  hideFooter?: boolean; // Added new prop to hide footer
  sx?: any; // Added ability to pass custom styles
}

const OrgDataTable: React.FC<DataTableProps> = ({
  columns,
  columnGroups,
  data,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortDirection,
  onSort,
  disableColumnMenu,
  disableColumnResize,
  renderActions,
  enableZebraStripes,
  hideFooter = false, // Default to showing footer
  sx = {},
}) => {
  // Convert columns to GridColDef format
  const gridColumns: GridColDef[] = [
    ...columns.map(column => ({
      ...column,
      field: column.id,
      headerName: column.label,
      flex: column.flex || 1,
    })),
    ...(renderActions
      ? [
          {
            field: 'actions',
            headerName: 'Action',
            sortable: false,
            width: 70,
            minWidth: 70,
            renderCell: params => renderActions(params.row),
          },
        ]
      : []),
  ];

  // Convert column groups to GridColumnGroupingModel format
  const gridColumnGroups: GridColumnGroupingModel = columnGroups
    ? columnGroups.map(group => ({
        groupId: group.id,
        headerName: group.label,
        children: columns.filter(col => col.groupId === group.id).map(col => ({ field: col.id })),
      }))
    : [];

  // Handle sorting
  const handleSortModelChange = (model: GridSortModel) => {
    if (!onSort) return;
    if (model.length > 0) {
      onSort({
        field: model[0].field,
        sort: model[0].sort,
      });
    } else {
      // Handle clearing sort
      onSort({
        field: '',
        sort: undefined,
      });
    }
  };

  // Handle pagination
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPageChange(model.page);
    if (model.pageSize !== rowsPerPage) {
      onRowsPerPageChange(model.pageSize);
    }
  };

  // Combine default styles with custom styles and footer hiding
  const combinedSx = {
    width: '100%',
    height: 'auto',
    boxShadow: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    ...(enableZebraStripes && {
      '& .MuiDataGrid-row:nth-of-type(odd)': {
        backgroundColor: 'rgba(245, 248, 255, 0.8)',
      },
    }),
    ...(hideFooter && {
      '& .MuiDataGrid-footerContainer': {
        display: 'none',
      },
    }),
    ...sx,
  };

  return (
    <Paper sx={combinedSx}>
      <DataGrid
        className={dataTableStyles.OrgdataTable}
        rows={data}
        columns={gridColumns}
        columnGroupingModel={gridColumnGroups}
        disableColumnMenu={disableColumnMenu}
        disableColumnResize={disableColumnResize}
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={{
          page: page,
          pageSize: rowsPerPage,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[5, 10, 25, 50]}
        sortingMode="server"
        sortModel={[
          {
            field: sortBy,
            sort: sortDirection,
          },
        ]}
        onSortModelChange={handleSortModelChange}
        columnGroupingEnabled={columnGroups && columnGroups.length > 0}
        disableRowSelectionOnClick
        autoHeight
      />
    </Paper>
  );
};

export default OrgDataTable;
